<script>
// Custom Components
import PageHeader from "@/components/page-header";
import iotService from "@/helpers/APIService/iot.service";
import errorHandler from "@/helpers/errorHandler";
import Middleware from "@/helpers/middlewares";
import { authComputed } from "@/state/helpers";
import appConfig from "@/app.config";

export default {
  page: {
    title: "rfid encoder",
    meta: [
      {
        name: "description",
        content: appConfig.description,
      },
    ],
  },
  data() {
    return {
      title: "List Of RFID Encoder",
      isLoading: false,
      rfidEncoderList: [],
      masterDataSetting: null,
      updateEncoderId: "",
    };
  },
  computed: {
    ...authComputed,
    deviceProps() {
      return {
        deviceId: this.deviceId,
        isEdit: this.isEdit,
        device: this.device,
      };
    },
  },
  components: {
    PageHeader,
  },
  mounted() {
    this.getRFIDEncoderList();
  },
  methods: {
    // Method is used to get device list
    async getRFIDEncoderList() {
      this.isLoading = true;
      try {
        const response = await iotService.getHotelRFIDEncoder();
        if (response) {
          this.rfidEncoderList = response;
          this.isLoading = false;
        }
      } catch (error) {
        this.isLoading = false;
        errorHandler(error);
      }
    },
    // Method is used to open meta data setting
    async viewMetaDataSetting() {
      this.$bvModal.show("master-data-setting");
      try {
        const response = await iotService.getLockMasterData();
        if (response) {
          this.masterDataSetting = response;
        }
      } catch (error) {
        errorHandler(error);
      }
    },
    // Method is used to update encoder port
    updateEncoder(id) {
      this.updateEncoderId = id;
    },
    // Method is used to update encoder
    updateEncoderPort(id, port, index) {
      iotService
        .updateHotelRFIDEncoder(id, { com_port: port })
        .then((response) => {
          this.$set(this.rfidEncoderList, index, response);
          Middleware.notification("Port successfully updated !!", "success");
        })
        .catch((error) => {
          errorHandler(error);
        });
      this.updateEncoderId = "";
    },
    // Method is used to copy activation code
    copyCode(code) {
      Middleware.copyCode(code);
    },
  },
};
</script>

<template>
  <div>
    <PageHeader
      :title="title"
      :action="viewMetaDataSetting"
      text="View Meta Data"
      :isButton="true"
    />
    <div class="card">
      <div class="card-body">
        <div class="table-responsive">
          <v-table
            :data="rfidEncoderList"
            class="table table-hover mb-0"
            ref="my-table"
          >
            <thead class="table-light" slot="head">
              <tr>
                <v-th sortKey="activation_code">Activation Code</v-th>
                <v-th sortKey="mqtt_device_id">Device Id</v-th>
                <v-th sortKey="mqtt_user">MQTT User</v-th>
                <v-th sortKey="com_port">Com Port</v-th>
                <v-th sortKey="is_active">Status</v-th>
                <th>Last Actived</th>
                <th>Created By</th>
              </tr>
            </thead>
            <tbody
              class="infinite-list"
              slot="body"
              slot-scope="{ displayData }"
              v-if="!isLoading && rfidEncoderList.length !== 0"
            >
              <tr v-for="(item, index) in displayData" :key="index">
                <td @click="copyCode(item.activation_code)">
                  <div class="overflow-tip cursor">
                    <span>{{ item.activation_code }}</span>
                  </div>
                </td>
                <td>
                  {{ item.mqtt_device_id }}
                </td>
                <td>
                  {{ item.mqtt_user }}
                </td>
                <td>
                  <b-form-input
                    v-if="updateEncoderId == item.id"
                    class="w-50"
                    id="port-input"
                    v-model="item.com_port"
                    autocomplete="off"
                    type="text"
                    @blur="updateEncoderPort(item.id, item.com_port, index)"
                  ></b-form-input>
                  <span @dblclick="updateEncoder(item.id)" v-else>
                    {{ item.com_port }}
                  </span>
                </td>
                <td>
                  {{ item.is_active ? "Active" : "De-Active" }}
                </td>
                <td>
                  {{
                    item.updated_at !== null
                      ? global_format_date_and_time(item.updated_at)
                      : "-"
                  }}
                </td>
                <td>
                  {{ item.created_by !== null ? item.created_by : "-" }}
                </td>
              </tr>
            </tbody>
            <tbody slot="body" v-else-if="isLoading">
              <tr v-for="item in 10" :key="item">
                <td class="placeholder-glow" v-for="no in 7" :key="no">
                  <span class="placeholder w-100 placeholder-lg"></span>
                </td>
              </tr>
            </tbody>
            <tbody slot="body" v-else>
              <tr>
                <td colspan="9" class="text-center">
                  <img
                    src="@/assets/images/empty.svg"
                    class="img-fluid"
                    alt=""
                  />
                  <p>
                    <span class="text-secondary">No Data Found</span>
                  </p>
                </td>
              </tr>
            </tbody>
          </v-table>
        </div>
      </div>
    </div>
    <!-- Model is used to display master config data -->
    <b-modal
      id="master-data-setting"
      centered
      title="Master data settting"
      hide-footer
      title-class="font-18"
    >
      <div class="row" v-if="masterDataSetting !== null">
        <div class="col-md-6">
          <h6>Hotel Key</h6>
          <p class="mb-0">{{ masterDataSetting.hotel_key }}</p>
        </div>
        <div class="col-md-6">
          <h6>Admin Key</h6>
          <p class="mb-0">{{ masterDataSetting.admin_key }}</p>
        </div>
        <hr class="my-2" />
        <div class="col-md-6">
          <h6>Key Code</h6>
          <p>{{ masterDataSetting.iot_support.key_code }}</p>
        </div>
        <div class="col-md-6">
          <h6>Mobile Key</h6>
          <p>{{ masterDataSetting.iot_support.mobile_key }}</p>
        </div>
        <div class="col-md-6">
          <h6>RFID Key</h6>
          <p>{{ masterDataSetting.iot_support.is_rfid_support }}</p>
        </div>
        <div class="col-md-6">
          <h6>Status</h6>
          <p>{{ masterDataSetting.is_active }}</p>
        </div>
        <div class="col-md-6">
          <h6>Created By</h6>
          <p>
            {{
              masterDataSetting.created_by !== null
                ? masterDataSetting.created_by
                : "-"
            }}
          </p>
        </div>
        <div class="col-md-6">
          <h6>Updated By</h6>
          <p>
            {{
              masterDataSetting.updated_by !== null
                ? masterDataSetting.updated_by
                : "-"
            }}
          </p>
        </div>
      </div>
    </b-modal>
  </div>
</template>
